import { useCallback, useContext, useMemo } from 'react';
import type { QuoteItem } from '@trinity/chat';
import { ChatCitationType } from '@trinity/chat';
import { TranscriptQuoteMomentIdContext, TranscriptToggleStarQuoteContext } from '@trinity/chat-components/containers/Transcript/context';
import { TranscriptGoodQuoteContainer } from '@trinity/chat-components/containers/Transcript/GoodQuoteContainer';
import { useCopyToClipboard } from '@trinity/chat-components/hooks/useCopyToClipboard';
import { CapabilitiesContext } from '../BrandInsights/context';
import { CitationsContext, ClickCitationContext } from './context';
import { Quote } from './Message.Response.Quote';

type Props = {
  className?: string;
  quote: QuoteItem;
};

const ChatMessageResponseQuoteContainer = ({
  className,
  quote,
}: Props) => {

  const handleToggleStarQuote = useContext(TranscriptToggleStarQuoteContext);
  const starred = !!useContext(TranscriptQuoteMomentIdContext);
  const capabilities = useContext(CapabilitiesContext);

  const citations = useContext(CitationsContext);
  const goToCitation = useContext(ClickCitationContext);

  const copyToClipboard = useCopyToClipboard();

  const handleCopyToClipboard = useCallback(() => {
    copyToClipboard(quote.value).catch(() => {});
  }, [copyToClipboard, quote.value]);

  const citationKey = useMemo(() => {

    const citation = citations.find(f => f.type === ChatCitationType.Transcript && f.id === quote.transcriptId.toString());

    if (!citation) return null;

    const snippetKey = `${citation.id}:${quote.startTs}:${quote.endTs}`;

    const snippet = quote.citationKey
      ? citation.snippets.find(f => f.id === quote.citationKey)
      : citation.snippets.find(f => f.id === snippetKey);

    if (!snippet) return null;

    const snippetAlpha = String.fromCharCode(96 + snippet.ordinal);

    return `${citation.ordinal}${snippetAlpha}`;
  }, [citations, quote]);

  const onViewCitation = useCallback(() => {
    if (!citationKey) return;

    goToCitation(citationKey);
  }, [citationKey, goToCitation]);

  return (
    <Quote
      className={className}
      citationValue={citationKey}
      onCopy={handleCopyToClipboard}
      onToggleStar={handleToggleStarQuote}
      onViewCitation={onViewCitation}
      starred={starred}
      canToggleStar={capabilities.canMarkGoodQuotes !== false}
      quote={quote} />
  );
};

const WithState = (props: Props) => {
  const range = useMemo(() => ({
    start: props.quote.startTs,
    end: props.quote.endTs,
  }), [props.quote.startTs, props.quote.endTs]);

  return (
    <TranscriptGoodQuoteContainer
      initialQuoteMomentId={props.quote.starredQuoteId}
      range={range}
      transcriptId={props.quote.transcriptId}>
      <ChatMessageResponseQuoteContainer {...props} />
    </TranscriptGoodQuoteContainer>
  );
};

export { WithState as ChatMessageResponseQuoteContainer };
